'use client';

import { useState, useEffect } from 'react';
import { Calculator, RefreshCw, Info, ChevronDown } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { vatCalculatorContent } from '../../data/vat-calculator-content';

type VatRate = {
    name: string;
    rate: number;
    country: string;
    currency?: string;
};

type Currency = {
    code: string;
    name: string;
    symbol: string;
    country: string;
};

const currencies: Currency[] = [
    { code: 'USD', name: 'US Dollar', symbol: '$', country: 'United States' },
    { code: 'EUR', name: 'Euro', symbol: '€', country: 'European Union' },
    { code: 'GBP', name: 'British Pound', symbol: '£', country: 'United Kingdom' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', country: 'Japan' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', country: 'Australia' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', country: 'Canada' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', country: 'Switzerland' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', country: 'China' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', country: 'India' },
    { code: 'RUB', name: 'Russian Ruble', symbol: '₽', country: 'Russia' },
];

const vatRates: VatRate[] = [
    { name: 'Standard Rate (20%)', rate: 20, country: 'UK', currency: 'GBP' },
    { name: 'Reduced Rate (5%)', rate: 5, country: 'UK', currency: 'GBP' },
    { name: 'Zero Rate (0%)', rate: 0, country: 'UK', currency: 'GBP' },
    { name: 'Standard Rate (19%)', rate: 19, country: 'Germany', currency: 'EUR' },
    { name: 'Standard Rate (20%)', rate: 20, country: 'France', currency: 'EUR' },
    { name: 'Standard Rate (22%)', rate: 22, country: 'Italy', currency: 'EUR' },
    { name: 'Standard Rate (21%)', rate: 21, country: 'Spain', currency: 'EUR' },
    { name: 'Standard Rate (25%)', rate: 25, country: 'Sweden', currency: 'SEK' },
    { name: 'Standard Rate (21%)', rate: 21, country: 'Netherlands', currency: 'EUR' },
    { name: 'Standard Rate (23%)', rate: 23, country: 'Poland', currency: 'PLN' },
    { name: 'Standard Rate (10%)', rate: 10, country: 'Japan', currency: 'JPY' },
    { name: 'Standard Rate (10%)', rate: 10, country: 'Australia', currency: 'AUD' },
    { name: 'Standard Rate (5%)', rate: 5, country: 'Canada', currency: 'CAD' },
    { name: 'Standard Rate (7.7%)', rate: 7.7, country: 'Switzerland', currency: 'CHF' },
    { name: 'Standard Rate (13%)', rate: 13, country: 'China', currency: 'CNY' },
    { name: 'Standard Rate (18%)', rate: 18, country: 'India', currency: 'INR' },
    { name: 'Standard Rate (20%)', rate: 20, country: 'Russia', currency: 'RUB' },
];

type CalculationType = 'add' | 'extract' | 'net' | 'gross';

export default function VatCalculator() {
    const [amount, setAmount] = useState<string>('');
    const [selectedRate, setSelectedRate] = useState<VatRate>(vatRates[0]);
    const [calculationType, setCalculationType] = useState<CalculationType>('add');
    const [netAmount, setNetAmount] = useState<number>(0);
    const [grossAmount, setGrossAmount] = useState<number>(0);
    const [vatAmount, setVatAmount] = useState<number>(0);
    const [customRate, setCustomRate] = useState<string>('');
    const [useCustomRate, setUseCustomRate] = useState<boolean>(false);
    const [isCalculated, setIsCalculated] = useState<boolean>(false);
    const [selectedCurrency, setSelectedCurrency] = useState<Currency>(currencies[0]);
    const [filteredVatRates, setFilteredVatRates] = useState<VatRate[]>([]);
    const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);

    const calculateVat = () => {
        const amountValue = parseFloat(amount) || 0;
        const rate = useCustomRate && customRate ? parseFloat(customRate) / 100 : selectedRate.rate / 100;

        let net = 0;
        let gross = 0;
        let vat = 0;

        switch (calculationType) {
            case 'add':
                net = amountValue;
                vat = net * rate;
                gross = net + vat;
                break;
            case 'extract':
                gross = amountValue;
                net = gross / (1 + rate);
                vat = gross - net;
                break;
            case 'net':
                net = amountValue;
                vat = net * rate;
                gross = net + vat;
                break;
            case 'gross':
                gross = amountValue;
                net = gross / (1 + rate);
                vat = gross - net;
                break;
        }

        setNetAmount(parseFloat(net.toFixed(2)));
        setGrossAmount(parseFloat(gross.toFixed(2)));
        setVatAmount(parseFloat(vat.toFixed(2)));
        setIsCalculated(true);
    };

    useEffect(() => {
        if (selectedCurrency) {
            const rates = vatRates.filter((rate) =>
                rate.currency === selectedCurrency.code ||
                (selectedCurrency.code === 'EUR' && !rate.currency)
            );
            setFilteredVatRates(rates.length > 0 ? rates : vatRates);

            if (rates.length > 0 && !rates.some((rate) => rate.rate === selectedRate.rate && rate.country === selectedRate.country)) {
                setSelectedRate(rates[0]);
            }
        }
    }, [selectedCurrency]);

    const resetForm = () => {
        setAmount('');
        setSelectedRate(filteredVatRates[0] || vatRates[0]);
        setCalculationType('add');
        setNetAmount(0);
        setGrossAmount(0);
        setVatAmount(0);
        setCustomRate('');
        setUseCustomRate(false);
        setIsCalculated(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        calculateVat();
    };

    const handleRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = filteredVatRates.find((rate) => rate.rate === parseFloat(e.target.value));
        if (selected) {
            setSelectedRate(selected);
        }
    };

    const handleCustomRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setCustomRate(value);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <ToolPageIntro title="VAT Calculator - Free Online Value Added Tax Calculator">
                Calculate VAT amounts, net prices, and gross amounts with our free online VAT calculator. Support for multiple countries and tax rates.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="calculationType" className="block text-sm font-medium text-gray-700 mb-1">
                                Calculation Type
                            </label>
                            <select
                                id="calculationType"
                                value={calculationType}
                                onChange={(e) => setCalculationType(e.target.value as CalculationType)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="add">Add VAT to net amount</option>
                                <option value="extract">Extract VAT from gross amount</option>
                                <option value="net">Calculate from net amount</option>
                                <option value="gross">Calculate from gross amount</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                                {calculationType === 'add' || calculationType === 'net' ? 'Net Amount' : 'Gross Amount'}
                            </label>
                            <div className="relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 sm:text-sm">{selectedCurrency.symbol}</span>
                                </div>
                                <input
                                    type="text"
                                    id="amount"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                                    placeholder="0.00"
                                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-lg"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <div>
                                    <label htmlFor="vatRate" className="block text-sm font-medium text-gray-700">
                                        VAT Rate
                                    </label>
                                    <p className="text-xs text-gray-500">for {selectedCurrency.country} ({selectedCurrency.code})</p>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        id="useCustomRate"
                                        type="checkbox"
                                        checked={useCustomRate}
                                        onChange={(e) => setUseCustomRate(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="useCustomRate" className="ml-2 block text-sm text-gray-700">
                                        Custom Rate
                                    </label>
                                </div>
                            </div>

                            {useCustomRate ? (
                                <div className="relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        value={customRate}
                                        onChange={handleCustomRateChange}
                                        placeholder="e.g., 20"
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-lg"
                                        required={useCustomRate}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 sm:text-sm">%</span>
                                    </div>
                                </div>
                            ) : (
                                <select
                                    id="vatRate"
                                    value={selectedRate.rate}
                                    onChange={handleRateChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {filteredVatRates.map((rate) => (
                                        <option key={`${rate.country}-${rate.rate}`} value={rate.rate}>
                                            {rate.name} - {rate.country}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="relative inline-block text-left mb-4">
                        <div>
                            <button
                                type="button"
                                className="inline-flex justify-between w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                            >
                                {selectedCurrency.name} ({selectedCurrency.code})
                                <ChevronDown className="ml-2 -mr-1 h-5 w-5" />
                            </button>
                        </div>

                        {isCurrencyOpen && (
                            <div className="origin-top-right absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                <div className="py-1 max-h-60 overflow-auto" role="menu" aria-orientation="vertical">
                                    {currencies.map((currency) => (
                                        <button
                                            key={currency.code}
                                            onClick={() => {
                                                setSelectedCurrency(currency);
                                                setIsCurrencyOpen(false);
                                            }}
                                            className={`${selectedCurrency.code === currency.code ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                                } group flex items-center px-4 py-2 text-sm w-full text-left`}
                                            role="menuitem"
                                        >
                                            {currency.name} ({currency.code})
                                            {selectedCurrency.code === currency.code && (
                                                <span className="ml-auto">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex space-x-4 pt-4">
                        <button
                            type="submit"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                        >
                            <Calculator className="h-5 w-5 mr-2" />
                            Calculate
                        </button>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
                        >
                            <RefreshCw className="h-5 w-5 mr-2" />
                            Reset
                        </button>
                    </div>
                </form>
            </div>

            {isCalculated && (
                <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-blue-800">Net Amount</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {selectedCurrency.symbol}{netAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-green-800">
                                VAT Amount ({useCustomRate ? `${customRate}%` : `${selectedRate.rate}%`})
                            </p>
                            <p className="text-2xl font-bold text-green-600">
                                {selectedCurrency.symbol}{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-purple-800">Gross Amount</p>
                            <p className="text-2xl font-bold text-purple-600">
                                {selectedCurrency.symbol}{grossAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <Info className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">About VAT Calculation</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                            <p>VAT (Value Added Tax) is a consumption tax placed on a product whenever value is added at each stage of the supply chain.</p>
                            <p className="mt-1">Standard VAT rates vary by country. Always check the current rates for your specific needs.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={vatCalculatorContent.title}
                    description={vatCalculatorContent.description}
                    content={vatCalculatorContent.content}
                    faqs={vatCalculatorContent.faqs}
                    relatedTools={vatCalculatorContent.relatedTools}
                />
            </div>
        </div>
    );
}
